import React, { useCallback } from 'react';
import { Button } from '@vibe/core';
import type ExcelJS from 'exceljs';
import type { PlannedVsActualReportResponse } from '../../api/report.api';
import type { PlannedVsActualView } from './PlannedVsActualHeader';
import {
  createWorkbook, addTitleRow, styleHeaderRow, styleGroupRow, styleDataRow, styleTotalRow,
  setColumnWidths, downloadWorkbook, getDeviationColor, formatDateForExcel, round, hexToArgb, COLORS,
} from '../../utils/export-report-excel';

interface Props {
  data: PlannedVsActualReportResponse;
  activeView: PlannedVsActualView;
}

function applyDeviationColor(
  cell: ExcelJS.Cell,
  deviation: number,
  originalEstimate: number,
  timeSpent: number,
): void {
  const color = getDeviationColor(deviation, originalEstimate, timeSpent);
  cell.font = { ...cell.font as Partial<ExcelJS.Font>, color: { argb: hexToArgb(color) }, bold: true };
}

const PlannedVsActualExportButton: React.FC<Props> = ({ data, activeView }) => {
  const handleExport = useCallback(async () => {
    try {
      const wb = createWorkbook();
      const ws = wb.addWorksheet('Planned vs Actual');

      addTitleRow(ws, 'Planned vs Actual Report', data.period.startDate, data.period.endDate);

      if (activeView === 'item') {
        // Item view
        const headers = [
          'Item Name', 'Assignee', 'Start', 'End',
          'Original Est. (h)', 'Time Spent (h)', 'Remaining (h)', 'Deviation (h)', 'Deviation %',
        ];
        const headerRow = ws.addRow(headers);
        styleHeaderRow(ws, headerRow);
        setColumnWidths(ws, [32, 20, 12, 12, 16, 14, 14, 14, 12]);

        for (const group of data.boardGroups) {
          const groupRow = ws.addRow([group.boardName, ...new Array(8).fill('')]);
          styleGroupRow(groupRow);

          for (const item of group.items) {
            const excelRow = ws.addRow([
              (item.isSubitem ? '\u21B3 ' : '') + item.itemName,
              item.assignees,
              formatDateForExcel(item.startDate),
              formatDateForExcel(item.endDate),
              round(item.originalEstimate),
              round(item.timeSpent),
              round(item.remainingEstimate),
              round(item.deviation),
              round(item.deviationPercent, 1),
            ]);
            styleDataRow(excelRow, item.isSubitem);

            for (let col = 5; col <= 8; col++) {
              excelRow.getCell(col).numFmt = '0.00';
              excelRow.getCell(col).alignment = { horizontal: 'center', vertical: 'middle' };
            }
            excelRow.getCell(9).numFmt = '0.00';
            excelRow.getCell(9).alignment = { horizontal: 'center', vertical: 'middle' };

            applyDeviationColor(excelRow.getCell(8), item.deviation, item.originalEstimate, item.timeSpent);
            applyDeviationColor(excelRow.getCell(9), item.deviation, item.originalEstimate, item.timeSpent);
          }
          // Summary row
          const s = group.summary;
          const summaryRow = ws.addRow([
            `SUMMARY FOR ${group.boardName}`,
            '',
            '',
            '',
            round(s.originalEstimate),
            round(s.timeSpent),
            round(s.remainingEstimate),
            round(s.deviation),
            round(s.deviationPercent, 1),
          ]);
          styleTotalRow(summaryRow);
          for (let col = 5; col <= 8; col++) {
            summaryRow.getCell(col).numFmt = '0.00';
            summaryRow.getCell(col).alignment = { horizontal: 'center', vertical: 'middle' };
          }
          summaryRow.getCell(9).numFmt = '0.00';
          summaryRow.getCell(9).alignment = { horizontal: 'center', vertical: 'middle' };
          applyDeviationColor(summaryRow.getCell(8), s.deviation, s.originalEstimate, s.timeSpent);
          applyDeviationColor(summaryRow.getCell(9), s.deviation, s.originalEstimate, s.timeSpent);
        }

        // Grand total
        const gt = data.grandTotal;
        const grandRow = ws.addRow([
          'GRAND TOTAL',
          '',
          '',
          '',
          round(gt.originalEstimate),
          round(gt.timeSpent),
          round(gt.remainingEstimate),
          round(gt.deviation),
          round(gt.deviationPercent, 1),
        ]);
        styleTotalRow(grandRow);
        grandRow.font = { bold: true, size: 11, color: { argb: hexToArgb(COLORS.primaryText) } };
        for (let col = 5; col <= 8; col++) {
          grandRow.getCell(col).numFmt = '0.00';
          grandRow.getCell(col).alignment = { horizontal: 'center', vertical: 'middle' };
        }
        grandRow.getCell(9).numFmt = '0.00';
        grandRow.getCell(9).alignment = { horizontal: 'center', vertical: 'middle' };
        applyDeviationColor(grandRow.getCell(8), gt.deviation, gt.originalEstimate, gt.timeSpent);
        applyDeviationColor(grandRow.getCell(9), gt.deviation, gt.originalEstimate, gt.timeSpent);
      } else {
        // Board view
        const headers = [
          'Board Name', 'Total Items', 'Total Assignees',
          'Original Est. (h)', 'Time Spent (h)', 'Remaining (h)', 'Deviation (h)', 'Deviation %',
        ];
        const headerRow = ws.addRow(headers);
        styleHeaderRow(ws, headerRow);
        setColumnWidths(ws, [32, 14, 16, 16, 14, 14, 14, 12]);

        for (const row of data.boardRows) {
          const excelRow = ws.addRow([
            row.boardName,
          String(row.totalItems),
          String(row.totalAssignees),
          round(row.originalEstimate),
          round(row.timeSpent),
          round(row.remainingEstimate),
          round(row.deviation),
          round(row.deviationPercent, 1),
          ]);
          styleDataRow(excelRow);

          for (let col = 4; col <= 7; col++) {
            excelRow.getCell(col).numFmt = '0.00';
            excelRow.getCell(col).alignment = { horizontal: 'center', vertical: 'middle' };
          }
          excelRow.getCell(8).numFmt = '0.00';
          excelRow.getCell(8).alignment = { horizontal: 'center', vertical: 'middle' };
          applyDeviationColor(excelRow.getCell(7), row.deviation, row.originalEstimate, row.timeSpent);
          applyDeviationColor(excelRow.getCell(8), row.deviation, row.originalEstimate, row.timeSpent);
        }

        // Grand total
        const gt = data.grandTotal;
        const grandRow = ws.addRow([
          'GRAND TOTAL',
        String(data.grandTotal.totalItems),
        String(data.grandTotal.totalAssignees),
        round(data.grandTotal.originalEstimate),
        round(data.grandTotal.timeSpent),
        round(data.grandTotal.remainingEstimate),
        round(data.grandTotal.deviation),
        round(data.grandTotal.deviationPercent, 1),
        ]);
        styleTotalRow(grandRow);
        grandRow.font = { bold: true, size: 11, color: { argb: hexToArgb(COLORS.primaryText) } };
        for (let col = 4; col <= 7; col++) {
          grandRow.getCell(col).numFmt = '0.00';
          grandRow.getCell(col).alignment = { horizontal: 'center', vertical: 'middle' };
        }
        grandRow.getCell(8).numFmt = '0.00';
        grandRow.getCell(8).alignment = { horizontal: 'center', vertical: 'middle' };
        applyDeviationColor(grandRow.getCell(7), gt.deviation, gt.originalEstimate, gt.timeSpent);
        applyDeviationColor(grandRow.getCell(8), gt.deviation, gt.originalEstimate, gt.timeSpent);
      }

      await downloadWorkbook(wb, `planned_vs_actual_${data.period.startDate}_${data.period.endDate}.xlsx`);
    } catch (err) {
      console.error('Excel export failed:', err);
    }
  }, [data, activeView]);

  return (
    <Button
      kind={Button.kinds.SECONDARY}
      size={Button.sizes.SMALL}
      onClick={handleExport}
    >
      Export Excel
    </Button>
  );
};

export default PlannedVsActualExportButton;
